import { useEffect, useState } from 'react';
import { UserCircle } from 'lucide-react';
import { api } from '../../lib/api';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';
import ContextCard from '../../components/ui/ContextCard';

export default function AccountPage() {
  const [profileEmail, setProfileEmail] = useState('');
  const [profileRole, setProfileRole] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.auth.me().then(({ user }) => {
      if (!user) return;
      setProfileEmail(user.email);
      setProfileRole(user.role);
    }).catch(console.error);
  }, []);

  async function changePassword() {
    if (!currentPassword || !newPassword) return;
    setSaving(true);
    setPasswordMsg(null);
    try {
      await api.auth.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setPasswordMsg({ ok: true, text: 'Contraseña actualizada.' });
    } catch (e) {
      setPasswordMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        icon={<UserCircle size={22} />}
        title="Mi cuenta"
        description="Revisa los datos de tu acceso y actualiza tu contraseña."
      />

      <ContextCard
        title="¿Qué puedes cambiar aquí?"
        description="Consultas el correo y el nivel de acceso de tu cuenta, y puedes actualizar tu contraseña."
        detail="Estos cambios afectan tu acceso al panel, no el contenido visible del sitio."
      />

      <div className="space-y-6 max-w-lg">
        <div className="bg-ui-surface border border-ui-border rounded-xl px-5 py-4 space-y-3">
          <div>
            <p className="text-sm text-ui-text-subtle">Correo</p>
            <p className="text-sm text-ui-text mt-0.5">{profileEmail}</p>
          </div>
          <div>
            <p className="text-sm text-ui-text-subtle">Nivel de acceso</p>
            <p className="text-sm text-ui-text capitalize mt-0.5">{profileRole}</p>
          </div>
        </div>

        <div className="bg-ui-surface border border-ui-border rounded-xl px-5 py-4 space-y-4">
          <p className="text-sm font-medium text-ui-text">Cambiar contraseña</p>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Contraseña actual</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
            />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Nueva contraseña</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
            />
          </div>
          {passwordMsg && (
            <p className={`text-sm ${passwordMsg.ok ? 'text-green-400' : 'text-red-400'}`}>
              {passwordMsg.text}
            </p>
          )}
          <button
            onClick={changePassword}
            disabled={saving || !currentPassword || !newPassword}
            className="px-4 py-2 bg-ui-accent text-ui-bg text-sm font-medium rounded-lg disabled:opacity-50 hover:bg-ui-accent-hover transition-colors"
          >
            {saving ? 'Guardando…' : 'Actualizar contraseña'}
          </button>
        </div>
      </div>
    </PageContainer>
  );
}
